/**
 * Home: what matters to this person today, by role (Andres, 2026-09-23: the old page felt generic).
 * - Firm members: each of their firms at a glance (baselines, program stage) and the items waiting on them.
 * - National IT and the Board: where the network stands in the program; the Board compares firms on Leadership.
 * - National reviewers: the National IT review queue.
 * Visual first, one line per item, detail one click away.
 */
import { Link, useOutletContext } from 'react-router-dom';
import { useApi, type Loadable } from '../api';
import { fullyText, inPlaceSummary, Ring } from '../charts';
import { Load } from '../components';
import { PROGRAM_NAME } from '@grc/shared/leadership-measures';
import type { LeadershipData } from '@grc/shared/vocabulary';
import type { FirmOverview, Me } from '../types';
import { nextStep, STAGE, StageBar } from './Program';
import { drafts } from './FirmOverview';
import { useQueueHome } from './Review';

const firstName = (name: string) => name.split(/[\s,]+/)[0] ?? name;
const pct = (v: number | null) => (v === null ? 'No results yet' : `${Math.round(v)}%`);

interface Todo {
  n: number;
  text: string;
  to: string;
}

/** Things waiting on the firm's IT lead, most useful first; nothing appears with a zero count. */
function todos(o: FirmOverview): Todo[] {
  const f = o.firmCode;
  const plansUncommitted = o.actionPlans.plans.filter((p) => p.deliveryStatus === 'not_yet_committed').length;
  const risksWithoutResponse = o.risks.list.filter((r) => !r.responseState && !r.accepted && (r.severity === 'very_high' || r.severity === 'high')).length;
  return [
    { n: o.selfAssessment.states.returned, text: o.selfAssessment.states.returned === 1 ? 'rating returned by National IT with questions' : 'ratings returned by National IT with questions', to: `/firm/${f}/controls?view=worklist&state=returned` },
    { n: drafts(o), text: drafts(o) === 1 ? 'rating not sent to National IT' : 'ratings not sent to National IT', to: `/firm/${f}/controls?view=worklist&state=draft` },
    { n: risksWithoutResponse, text: 'higher risks without a response', to: `/firm/${f}/risks` },
    { n: plansUncommitted, text: 'action plans without an owner and date', to: `/firm/${f}?view=plans` },
    { n: o.source === 'self' ? o.selfAssessment.states.not_rated : 0, text: o.selfAssessment.states.not_rated === 1 ? 'control still to rate' : 'controls still to rate', to: `/firm/${f}/controls?view=worklist&state=not_rated` },
  ].filter((t) => t.n > 0);
}

function FirmSnapshot({ firmCode }: { firmCode: string }) {
  const state = useApi<FirmOverview>(`/firms/${firmCode}/overview`);
  return (
    <Load state={state} what="your firm">
      {(o) => {
        const list = o.canUpdate ? todos(o) : [];
        const p = o.program;
        return (
          <section className="card snapshot" aria-labelledby={`snap-${firmCode}`}>
            <header className="snapshot-head">
              <h2 id={`snap-${firmCode}`}>{o.firmName}</h2>
              <Link className="pill" to={`/firm/${firmCode}`}>
                Open dashboard
              </Link>
            </header>
            <div className="snapshot-body">
              <div className="snapshot-rings">
                {o.baselines ? (
                  [o.baselines.btc, o.baselines.bti].map((b) => {
                    // Current results carry the split; the March 2025 survey (achieved or not) does not.
                    const partlyPts = b.partly !== undefined && b.assessed ? (b.partly / b.assessed) * 100 : null;
                    return (
                    <figure key={b.name} className="mini-ring">
                      <Ring
                        size={96}
                        pct={b.pct}
                        partly={partlyPts}
                        sub={partlyPts !== null && b.pct !== null ? fullyText(b.pct, partlyPts) : undefined}
                        median={b.networkMedian}
                        label={`${b.name}: ${pct(b.pct)}, ${b.inPlace} of ${b.assessed} in place${partlyPts !== null ? `. ${inPlaceSummary(b.pct, partlyPts)}` : ''}`}
                      />
                      <figcaption>
                        <strong>{b.name}</strong>
                        <span className="muted small">
                          {/* A firm still rating its controls has fewer rated than the baseline holds: say so (bug fix, 2026-10-03). */}
                          {'size' in b && b.assessed < b.size ? `${b.inPlace} of ${b.assessed} rated so far in place (${b.size} in all)` : `${b.inPlace} of ${b.assessed} in place`}
                        </span>
                      </figcaption>
                    </figure>
                    );
                  })
                ) : (
                  <p className="muted">No results yet. Start with the self-assessment.</p>
                )}
              </div>
              <div className="snapshot-side">
                {p && (
                  <div className="snapshot-program">
                    <span className={`chip tone-${STAGE[p.stage].tone}`}>{STAGE[p.stage].label}</span>
                    <p className="small">{nextStep(p)}</p>
                  </div>
                )}
                {o.canUpdate &&
                  (list.length ? (
                    <ul className="plain todo">
                      {list.map((t) => (
                        <li key={t.text}>
                          <Link to={t.to}>
                            <span className="todo-n">{t.n}</span> {t.text}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="small ok">Nothing is waiting on you.</p>
                  ))}
              </div>
            </div>
          </section>
        );
      }}
    </Load>
  );
}

/**
 * The network at a glance, from Leadership's network figures, which National IT and the Board both read (the
 * National view is National IT's only, DEC-33). The Board's link opens Leadership's network tab (DEC-22).
 */
function NetworkSnapshot({ boardOnly }: { boardOnly: boolean }) {
  // for=home: audited as Home's network card, not as a Leadership view (CP-05).
  const state = useApi<LeadershipData>('/leadership?firm=network&for=home');
  return (
    <Load state={state} what="the network">
      {({ network: n }) => {
        const staged = (n.programFirms ?? []).filter((f) => f.stage !== null);
        return (
          <section className="card snapshot" aria-labelledby="snap-network">
            <header className="snapshot-head">
              <h2 id="snap-network">The network</h2>
              <Link className="pill" to={boardOnly ? '/leadership?view=network' : '/national'}>
                {boardOnly ? 'Compare firms on Leadership' : 'Open the National view'}
              </Link>
            </header>
            <p className="snapshot-stat">
              <strong>{n.firmsWithResults}</strong> of {n.firmsInNetwork} firms have CDW assessment results
            </p>
            {staged.length > 0 && (
              <>
                <h3 className="small">{PROGRAM_NAME}</h3>
                <StageBar firms={staged} />
              </>
            )}
          </section>
        );
      }}
    </Load>
  );
}

function ReviewSnapshot() {
  // National IT reviewers open the queue on the National view's Reviews tab (5D B); other reviewers on its own page.
  const home = useQueueHome();
  const state = useApi<{ submissions: unknown[] }>('/review/submissions?status=submitted');
  return (
    <Load state={state} what="the review queue">
      {(q) => (
        <section className="card snapshot compact" aria-labelledby="snap-review">
          <h2 id="snap-review">Ratings to review</h2>
          <p className="snapshot-stat">
            {q.submissions.length ? (
              <>
                <strong>{q.submissions.length}</strong> {q.submissions.length === 1 ? 'rating waits' : 'ratings wait'} for National IT review
              </>
            ) : (
              'No ratings wait for National IT review'
            )}
          </p>
          <Link to={home}>Open the review queue</Link>
        </section>
      )}
    </Load>
  );
}

const SHORTCUTS = [
  { to: '/guidance', title: 'Security controls', text: 'The 63 controls, in plain words, with how to put each in place' },
  { to: '/guidance/policies', title: 'Policy templates', text: 'Baseline policies in the Baker Tilly format' },
  { to: '/guidance/resources', title: 'How National can help', text: 'Shared services, frameworks and vendors' },
];

export function Home() {
  const me = useOutletContext<Loadable<Me>>();
  return (
    <Load state={me} what="your access">
      {(m) => {
        const network = m.tiers.national || m.tiers.board;
        return (
          <>
            <header className="page-header home-head">
              <p className="eyebrow">Network-wide Cyber Resilience &amp; Risk Reduction Program</p>
              <h1>Welcome back, {firstName(m.user.name)}</h1>
            </header>
            {m.groupsOverage && <p className="notice">You belong to many groups, so some access could not be confirmed. Please contact National IT.</p>}
            {/* The three guidance links come first (DEC-49), then what is waiting on this person. */}
            <nav className="shortcuts" aria-label="Guidance">
              {SHORTCUTS.map((s) => (
                <Link key={s.to} to={s.to} className="shortcut">
                  <strong>{s.title}</strong>
                  <span className="muted small">{s.text}</span>
                </Link>
              ))}
            </nav>
            <div className="home-grid">
              {m.firms.map((f) => (
                <FirmSnapshot key={f.firmCode} firmCode={f.firmCode} />
              ))}
              {network && <NetworkSnapshot boardOnly={!m.tiers.national} />}
              {m.tiers.reviewer && <ReviewSnapshot />}
              {!m.firms.length && !network && !(m.formerFirms ?? []).length && (
                <section className="card snapshot">
                  <h2>Your firm</h2>
                  <p>You are not in a firm group yet. Your firm's IT lead can add you to its CIS Dashboard group.</p>
                </section>
              )}
            </div>
          </>
        );
      }}
    </Load>
  );
}
